import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowRight, CheckCircle, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { services, testimonials } from '../mockData';

const Home = () => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  
  const features = [
    { title: 'Fullständig konfidentialitet', desc: 'När du anlitar oss är ditt ärende helt skyddat. Ingen utomstående får veta att du kontaktat oss och all kommunikation sker diskret. Din integritet är alltid vår högsta prioritet.' },
    { title: 'Moderna och effektiva metoder', desc: 'Vi kombinerar klassisk detektivkunskap med digital spaning, OSINT och avancerad analys. Det gör att vi kan arbeta snabbare, smartare och leverera konkreta resultat.' },
    { title: 'Bevis som håller juridiskt', desc: 'All dokumentation vi samlar in följer svensk lag och kan användas i juridiska sammanhang vid behov. Du får en professionell rapport med tydliga bevis och analys.' },
    { title: 'Snabb återkoppling och start', desc: 'Vi återkommer oftast inom 24 timmar och kan i många fall påbörja utredningar samma dag. Akuta ärenden prioriteras alltid.' },
    { title: 'Tydliga och transparenta priser', desc: 'Du får alltid en klar och tydlig offert innan vi påbörjar arbetet. Inga dolda avgifter eller oväntade kostnader.' },
    { title: 'Vi arbetar i hela Sverige', desc: 'Oavsett var ditt ärende finns kan vi snabbt mobilisera resurser och genomföra uppdrag över hela landet.' },
    { title: 'Diskret rådgivning utan förpliktelser', desc: 'Är du osäker på om du behöver en utredning? Kontakta oss för en kostnadsfri och konfidentiell första bedömning.' },
    { title: 'Erfarenhet och expertis', desc: 'Vi har erfarenhet av att hantera både enkla och komplexa ärenden för privatpersoner och företag. Du får en strukturerad och professionell utredning där inget lämnas åt slumpen.' },
    { title: 'Tydlig kommunikation genom hela processen', desc: 'Du hålls uppdaterad under hela utredningen och vet alltid vad som händer i ditt ärende. Vi är tillgängliga för frågor och ser till att du känner dig trygg genom hela processen.' }
  ];

  const visibleFeatures = showAllFeatures ? features : features.slice(0, 3);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/5 rotate-45 rounded-3xl blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/5 -rotate-45 rounded-3xl blur-3xl"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(20, 184, 166, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(20, 184, 166, 0.03) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 md:pt-0">
          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full text-teal-400 text-sm font-medium">
              Diskreta utredningar i hela Sverige
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="text-white">Svar när du</span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
              behöver veta
            </span>
          </h1>
          
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8 leading-relaxed">
            Professionell detektivbyrå med verksamhet över hela Sverige. Vi hjälper privatpersoner och företag med diskreta utredningar, spårningar och informationsinsamling.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/tjanster"
              className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-teal-500/50 transition-all duration-300 flex items-center space-x-2"
            >
              <span>Utforska våra tjänster</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/kontakt"
              className="px-8 py-4 bg-white/5 backdrop-blur-sm border border-white/10 text-white rounded-lg font-medium hover:bg-white/10 transition-all duration-300"
            >
              Kontakta oss
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">5+</div>
              <div className="text-gray-400 text-sm">År av erfarenhet</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">500+</div>
              <div className="text-gray-400 text-sm">Nöjda kunder</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">24/7</div>
              <div className="text-gray-400 text-sm">Tillgänglig support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Varför välja oss?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Vi levererar resultat genom erfarenhet, diskretion och modern teknik
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visibleFeatures.map((item, index) => (
              <div key={index} className="p-6 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-teal-500/50 transition-all duration-300 group">
                <CheckCircle className="w-10 h-10 text-teal-400 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          
          {features.length > 3 && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowAllFeatures(!showAllFeatures)}
                className="px-6 py-3 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 transition-all duration-300"
              >
                {showAllFeatures ? 'Visa mindre' : 'Visa mer'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Våra tjänster
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Diskreta utredningar anpassade för både privatpersoner och företag
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 3).map((service) => (
              <div key={service.id} className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-teal-500/50 transition-all duration-300 group">
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-teal-400 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                  {service.description}
                </p>
                <div className="mb-4">
                  <div className="text-teal-400 font-semibold">Kontakta oss för offert</div>
                </div>
                <Link
                  to="/tjanster"
                  className="inline-flex items-center text-teal-400 hover:text-teal-300 text-sm font-medium group-hover:translate-x-1 transition-transform"
                >
                  Läs mer <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/tjanster"
              className="inline-flex items-center px-6 py-3 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 transition-all duration-300"
            >
              Se alla tjänster <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials - Auto-scrolling Carousel */}
      <section className="py-20 bg-slate-900/50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Vad våra kunder säger
            </h2>
          </div>

          {/* Scrolling testimonials */}
          <div className="relative">
            <div className="flex animate-scroll gap-6">
              {/* First set */}
              {testimonials.map((testimonial) => (
                <div 
                  key={testimonial.id} 
                  className="flex-shrink-0 w-80 md:w-96 bg-slate-800/50 border border-slate-700 rounded-xl p-6"
                >
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-teal-400 text-teal-400" />
                    ))}
                  </div>
                  <p className="text-gray-400 mb-4 leading-relaxed">{testimonial.text}</p>
                  <div>
                    <div className="font-semibold text-white">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
              ))}
              {/* Duplicate set for seamless loop */}
              {testimonials.map((testimonial) => (
                <div 
                  key={`dup-${testimonial.id}`} 
                  className="flex-shrink-0 w-80 md:w-96 bg-slate-800/50 border border-slate-700 rounded-xl p-6"
                >
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-teal-400 text-teal-400" />
                    ))}
                  </div>
                  <p className="text-gray-400 mb-4 leading-relaxed">{testimonial.text}</p>
                  <div>
                    <div className="font-semibold text-white">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal-600/20 to-emerald-600/20" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 text-teal-400 mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-white mb-4">
            Redo att ta nästa steg?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Kontakta oss idag för en kostnadsfri konsultation och offert
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-teal-500/50 transition-all duration-300"
          >
            Boka konsultation <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
